/**
 * Pure helper for the unit a recipe line is rendered in (ATL-1012).
 *
 * Kept in a standalone, dependency-free module for the same reason as `ingredientQuantity.js`:
 * `ingredientCalculations.js` imports `AtomsProvider`, and that import chain is not loadable under
 * jest, so anything living there cannot be unit-tested.
 */

/**
 * The unit a recipe line should be rendered in.
 *
 * ATL-1012 — A RECIPE LINE IS A REFERENCE, NOT A SNAPSHOT, AND THE TWO READ MODES DISAGREED ABOUT IT.
 *
 * `{id, name, quantity}` is what the backend DTO (`create-product.dto.ts` IngredientDto), the
 * generated Zod schema, the declared frontend type (`global/Firebase/firestore.types.ts:145`) and
 * every other consumer already treat an ingredient as. Firestore is the outlier: it stores a
 * denormalised copy of the ingredient product's whole document, including `productUnit` — and the
 * frontend itself strips that back to the three fields on every write
 * (`global/Firebase/products.ts:349-357` create, `:1040-1048` update).
 *
 * So `ingredient.productUnit` is present in Firestore read mode and absent in Supabase read mode.
 * `IngredientsView` read it directly, so at the read cutover a 0.357 kg ingredient started
 * rendering as "0.357 шт" — the unit silently changing under a merchant is exactly the class of
 * quiet wrongness this migration is not allowed to ship.
 *
 * Resolving from the product first fixes that, and is lossless where the product resolves.
 * Measured over EVERY recipe line in prod on 2026-08-30 — 6,485 lines, 5,793 of which still carry
 * a frozen `productUnit`:
 *
 *   snapshot unit != referenced product's base_unit, product resolvable :   0
 *   lines whose ingredient product is not in `products` at all          : 110 (70 carrying a unit)
 *
 * The frozen unit has never once disagreed with the live one; every apparent difference is a line
 * pointing at a product that is not there. Hence the fallback: for an ingredient no longer in the
 * catalogue the snapshot is the only thing left that knows the unit, and throwing it away to render
 * "шт" would be a second bug wearing the first one's clothes.
 *
 * @param {Object} ingredient  a recipe line: `{id, name, quantity}`, possibly with legacy extras
 * @param {Object} productsDict  the live catalogue, keyed by product id
 * @returns {string|undefined} the product unit id, or `undefined` when nothing knows it
 */
export const resolveIngredientUnit = (ingredient, productsDict) => {
  const live = productsDict?.[ingredient?.id]?.productUnit
  // `??` and not `||`: a unit id is opaque text and an empty string is not a unit, but neither is
  // it a reason to reach past a product that answered.
  return live ?? ingredient?.productUnit
}

